import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useIsSuperAdmin } from "@/hooks/useIsSuperAdmin";

const PLAN_NAMES: Record<string, string> = {
  BASIC: "Basic",
  PRO: "Pro",
  ENTERPRISE: "Enterprise",
};

export interface OrgQuota {
  planType: string;
  planLabel: string;
  limit: number;
  used: number;
  remaining: number;
  /** true when the org has consumed its full monthly allowance */
  isAtLimit: boolean;
  /** percentage consumed (0-100, capped) */
  pct: number;
  isLoading: boolean;
  refetch: () => void;
}

export function useOrgQuota(): OrgQuota {
  const { orgId } = useAuth();
  const { isSuperAdmin } = useIsSuperAdmin();

  const {
    data: orgInfo,
    isLoading: orgLoading,
  } = useQuery({
    queryKey: ["org-quota-info", orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizaciones")
        .select("plan_type, monthly_doc_limit")
        .eq("id", orgId!)
        .single();
      if (error) throw error;
      return data as { plan_type: string; monthly_doc_limit: number };
    },
    enabled: !!orgId,
    staleTime: 60_000,
  });

  const {
    data: used = 0,
    isLoading: countLoading,
    refetch,
  } = useQuery({
    queryKey: ["org-doc-count", orgId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_current_month_doc_count", {
        _org_id: orgId!,
      });
      if (error) throw error;
      return (data as number) ?? 0;
    },
    enabled: !!orgId,
    staleTime: 30_000,
  });

  // Super admins acceden a TODAS las funcionalidades (plan ENTERPRISE virtual, sin tope).
  const rawPlanType = orgInfo?.plan_type ?? "BASIC";
  const rawLimit = orgInfo?.monthly_doc_limit ?? 100;
  const planType = isSuperAdmin ? "ENTERPRISE" : rawPlanType;
  const limit = isSuperAdmin ? Number.POSITIVE_INFINITY : rawLimit;
  const remaining = isSuperAdmin ? Number.POSITIVE_INFINITY : Math.max(rawLimit - used, 0);
  const pct = isSuperAdmin ? 0 : rawLimit > 0 ? Math.min((used / rawLimit) * 100, 100) : 0;

  return {
    planType,
    planLabel: isSuperAdmin ? "Enterprise (Super Admin)" : PLAN_NAMES[planType] ?? planType,
    limit,
    used,
    remaining,
    isAtLimit: isSuperAdmin ? false : used >= rawLimit,
    pct,
    isLoading: orgLoading || countLoading,
    refetch,
  };
}
